import { BLAZOR_ASSEMBLY, RENDERABLE_ELEMENTS } from "./constants";

/**
 * Creates a debounced function with a period of {@link delay}. Repeated calls to the function won't trigger until {@link delay}
 * has elapsed. You cannot get the return value for the debounced function.
 * @param fn The function to debounce.
 * @param delay How long until {@link fn} is actually called. Repeated calls until then are ignored.
 * @returns The debounced form of {@link fn}
 */
export function debounce<F extends (...params: Parameters<F>) => void>(fn: F, delay: number = 300) {
    let timeoutID: number = null;
    return function (this: any, ...args: Parameters<F>) {
        clearTimeout(timeoutID);
        timeoutID = window.setTimeout(() => fn.apply(this, args), delay);
    };
}

export function HtmlCollectionToRenderablesArray(children: HTMLCollection, includeGroups: boolean = false): SVGGraphicsElement[] {
    function ElementToRenderablesFilter(el: Element): el is SVGGraphicsElement {
        const tagName = el.tagName.toLowerCase();
        return RENDERABLE_ELEMENTS.indexOf(tagName) >= 0 || (includeGroups && tagName === "g")
    }

    return Array.from(children).filter(ElementToRenderablesFilter);
}

/**
 * Trims single and double quotes from the beginning and end of the string.
 * @param str   The string to trim.
 * @returns     The trimmed string.
 */
export function trimQuotes(str: string) {
    return str.replace(/^["']+(.+?(?=["']+$))["']+$/, "$1");
}

/**
 * Trims the specified character from the beginning and end of the string.
 */
export function trim(str: string, ch: string) {
    let start = 0;
    let end = str.length;

    while(start < end && str[start] === ch) {
        ++start;
    }

    while(end > start && str[end - 1] === ch) {
        --end;
    }

    return (start > 0 || end < str.length) ? str.substring(start, end) : str;
}

/**
 * Trims the specified character from the beginning of the string.
 */
export function trimStart(str: string, ch: string) {
    let start = 0;
    const end = str.length;

    while(start < end && str[start] === ch) {
        ++start;
    }

    return start > 0  ? str.substring(start) : str;
}

/**
 * Trims the specified character from the end of the string.
 */
export function trimEnd(str: string, ch: string) {
    const start = 0;
    let end = str.length;

    while(end > 0 && str[end - 1] === ch) {
        --end;
    }

    return end < str.length ? str.substring(0, end) : str;
}

/**
 * Trims any of the characters in the array from the beginning and end of the string.
 */
export function trimAny(str: string, chars: string[]) {
    let start = 0;
    let end = str.length;

    while(start < end && chars.indexOf(str[start]) >= 0) {
        ++start;
    }

    while(end > start && chars.indexOf(str[end - 1]) >= 0) {
        --end;
    }

    return (start > 0 || end < str.length) ? str.substring(start, end) : str;
}

/**
 * Trims any of the characters in the array from the beginning of the string.
 */
export function trimStartAny(str: string, chars: string[]) {
    let start = 0;
    const end = str.length;

    while(start < end && chars.indexOf(str[start]) >= 0) {
        ++start;
    }

    return start > 0 ? str.substring(start) : str;
}

/**
 * Trims any of the characters in the array from the end of the string.
 */
export function trimEndAny(str: string, chars: string[]) {
    const start = 0;
    let end = str.length;

    while(end > start && chars.indexOf(str[end - 1]) >= 0) {
        --end;
    }

    return end < str.length ? str.substring(0, end) : str;
}


export function getPanelPlaceholderID(panel: PanelDesigner.IPanel) {
    return `PANEL__${panel.panelTypeID}__${panel.id}`
}

/**
 * Gets the value of an attribute or - if missing - child element of an element.
 */
export function getXmpValue(element: Element, attributeOrChildName: string, defaultValue: string | null = null) {
    return element.getAttribute(attributeOrChildName) 
        ?? element.querySelector(attributeOrChildName)?.textContent 
        ?? defaultValue;
}

/**
 * Gets the value of an attribute or - if missing - child element of an element.
 */
export function getXmpValueNS(element: Element, namespace: string, attributeOrChildName: string, defaultValue: string | null = null) {
    return element.getAttributeNS(namespace, attributeOrChildName) 
        ?? element.querySelector(attributeOrChildName)?.textContent 
        ?? defaultValue;
}

export function blazorAlert(title: string, message: string, okButtonLabel: string = "Ok"): Promise<never> {
    return DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, "Alert", title, message, okButtonLabel);
}

export function blazorConfirm(title: string, message: string, okButtonLabel: string = "Ok", cancelButtonLabel: string = "Cancel"): Promise<boolean> {
    return DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, "Confirm", title, message, okButtonLabel, cancelButtonLabel);
}

export function blazorPrompt(title: string, message: string, initialValue: string | null = null, okButtonLabel: string = "Ok", cancelButtonLabel: string = "Cancel"): Promise<string | null> {
    return DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, "Prompt", title, message, initialValue, okButtonLabel, cancelButtonLabel);
}

export function blazorOpenPersistentAlert(title: string, message: string): Promise<never> {
    return DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, "OpenPersistentAlert", title, message);
}

export function blazorClosePersistentAlert(): Promise<never> {
    return DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, "ClosePersistentAlert");
}

export function blazorShowToast(type: "info" | "success" | "warning" | "error", content: string, timeout: number = -1.0): Promise<string> {
    return DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, "ShowToast", type, content, timeout);
}

export function blazorHideToast(toastID: string): Promise<never> {
    return DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, "HideToast", toastID);
}

export function getConsolidatedTransformMatrix(element: SVGGraphicsElement) {
    const allTransforms = element.transform.baseVal;
    let matrix = element.ownerSVGElement.createSVGMatrix();
    for (let i = 0; i < allTransforms.length; i++) {
        matrix = matrix.multiply(allTransforms.getItem(i).matrix);
    }
    return matrix;
}

export async function* iterateReadableStream(stream: ReadableStream) {
    const reader = stream.getReader();
    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            return;
        } else {
            yield value;
        }
    }
}

const textDecoder = new TextDecoder();
export async function* iterateReadableStreamAsString(stream: ReadableStream) {
    for await (const chunk of iterateReadableStream(stream)) {
        yield textDecoder.decode(chunk);
    }
}

/**
 * Converts the supplied HTML or SVG element to a JSON representation that can then
 * be used within Blazor.
 * @param maxChildrenDepth How many levels of children to include.
 * @param includeText Whether to include `innerText`. This is the text of the element's entire subtree, and is repeated
 *     again for every included child, so it can easily dwarf the rest of the payload.
 */
export function elementToJson(element: Element, maxChildrenDepth: number = 256, includeText: boolean = true) {
    // TODO: Also include SVG positioning information.
    let innerText: string | null = null;
    if (includeText) {
        if ('textContent' in element) {
            innerText = element.textContent;
        } else if ('innerText' in element) {
            innerText = element.innerText;
        }
    }

    return {
        type: element.tagName.toLowerCase(),
        id: element.id,
        attributes: Array.from(element.attributes).reduce((acc, attr) => {
            acc[attr.name] = attr.value;
            return acc;
        }, {} as Record<string, string>),
        children: maxChildrenDepth > 0 ? Array.from(element.children).map(child => elementToJson(child, maxChildrenDepth - 1, includeText)) : [],
        innerText,
    }
}
